import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, LabelList
} from 'recharts';
import { fetchMachineWatch2, fetchMachineDrive, fetchMachineAlarm } from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import './DataLog.css';

/* ===== 데이터 · 로그 탭 — Figma「사출기 > 데이터 · 로그」(샷로그 / 구동부 작동이력 / 알람 이력 / 알람 분석) =====
 * 스타일은 dl- 접두어만 쓴다. 기존 감시2 · 구동부 · 알람 탭의 API 를 그대로 쓴다.
 *
 * 데이터 출처
 * - 샷로그       : /machine/watch2/{id}?limit=N — 최근 N샷의 사이클타임·보압시간·계량시간·사출시작·보압시작·쿠션량·취출시간·품번(기존 감시2 탭)
 *                  필터는 모두 화면에서 처리한다. 공장·설비는 헤더의 사출기 선택과 같은 목록(Machine.jsx 의 factoryList / allMachines)을 쓰고
 *                  설비를 바꾸면 헤더 선택도 함께 바뀐다. 제품명(금형)·기간·샷 범위는 불러온 샷의 품번·샷 시각·샷카운트에서 만든다.
 *                  "이탈"은 백엔드에 한계값이 없어 불러온 샷의 열별 평균 ±3σ 를 벗어난 값으로 화면에서 판정한다(추정 표기).
 * - 구동부 작동이력: /dashboard/machine/{id}/drive 의 driveHistory (시간·구동부·동작·압력·속도·위치). 기간은 이력의 날짜로 화면 필터.
 *                  심각도는 구동부 이력에 없는 정보라 셀렉트를 두되 "전체"만 있고 비활성이다.
 * - 알람 이력      : /dashboard/machine/{id}/alarm 의 summary(심각도별 건수) + alarmHistory (시간·심각도·코드·메시지·설정값·실행값·상태)
 *                  시안의 "해결 시각"은 API 에 없어 상태(진행/해결)만 표시한다.
 * - 알람 분석      : 같은 응답의 paretoAnalysis (코드별 건수·누적 비율) + timeline (일자별 건수, MM/dd 30일)
 *                  막대색: 파레토는 그 코드의 알람 심각도(불량=CRITICAL 빨강 / 그 외 에러·경고 노랑, 이력에서 조회),
 *                          타임라인은 그날 알람 이력의 최고 심각도(불량 빨강 / 주의 노랑 / 그 외 회색)
 * 값이 null 이면 '—'. */

const isNum = (v) => v != null && !Number.isNaN(Number(v));
const num = (v, digits = 2) => (isNum(v) ? Number(v).toFixed(digits) : '—');
const intFmt = (v) => (isNum(v) ? Number(v).toLocaleString() : '—');
const SIGMA_K = 3; // 샷로그 "이탈" 판정 배수 (평균 ± kσ, 추정)
const SHOT_RANGE_SIZE = 100; // 샷 범위 셀렉트의 구간 크기

/* "2026-09-28T09:25:44" → "09-28 09:25:44" / "HH:mm:ss" / "2026-09-28" / "09/28" */
const dateParts = (v) => (v ? String(v).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):?(\d{2})?/) : null);
const fmtDateTime = (v) => {
  const m = dateParts(v);
  if (!m) return v ? String(v) : '—';
  return `${m[2]}-${m[3]} ${m[4]}:${m[5]}${m[6] ? `:${m[6]}` : ''}`;
};
const fmtTime = (v) => {
  const m = dateParts(v);
  return m ? `${m[4]}:${m[5]}${m[6] ? `:${m[6]}` : ''}` : (v ? String(v) : '—');
};
const dateOf = (v) => { const m = dateParts(v); return m ? `${m[1]}-${m[2]}-${m[3]}` : null; };
const monthDayOf = (v) => { const m = dateParts(v); return m ? `${m[2]}/${m[3]}` : null; };

/* CSV 내려받기 — 필터된 행 그대로, 엑셀용 UTF-8 BOM */
const downloadCsv = (filename, headers, rows) => {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const SUB_TABS = ['shots', 'drive', 'alarms', 'analysis'];
/* 샷로그 열 — key 는 Watch2Response.Watch2Data 필드, labelKey 는 기존 감시2 탭 번역(components.monitoringWatch2.cols) 재사용 */
const SHOT_COLUMNS = [
  { key: 'cycleTime', labelKey: 'cycleTime', unit: 's' },
  { key: 'holdTime1', labelKey: 'injHoldTime', unit: 's' },
  { key: 'chargeTime', labelKey: 'chargeTime', unit: 's' },
  { key: 'injStartPosi', labelKey: 'injStartPosi', unit: 'mm' },
  { key: 'turnPosi', labelKey: 'turnPosi', unit: 'mm' },
  { key: 'injEndPosi', labelKey: 'injEndPosi', unit: 'mm' },
  { key: 'fetchTime', labelKey: 'fetchTime', unit: 's' }
];
const DRIVE_COLUMNS = ['time', 'driveType', 'action', 'pressure', 'speed', 'position'];
const ALARM_COLUMNS = ['occurredAt', 'severity', 'code', 'message', 'settingValue', 'actualValue', 'status'];
const SHOT_LIMITS = [100, 200, 500, 1000];
const SAMPLE_INTERVALS = [1, 2, 5, 10];
const SEVERITIES = ['CRITICAL', 'WARNING', 'INFO'];
const SEVERITY_TONE = { CRITICAL: 'critical', WARNING: 'warning', INFO: 'info' };
/* 디자인 지정색: Error #F04438 / Warning #FDB022, 그 외 막대 #74858E, 누적선 #83A6FF */
const COLORS = { err: '#F04438', wrn: '#FDB022', base: '#74858E', line: '#83A6FF' };
const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: '#1A1E24', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', fontSize: '12px' },
  labelStyle: { color: '#98A8BF', fontSize: '12px' }
};
const AXIS_TICK = { fill: '#98A8BF', fontSize: 11 };
const AXIS_LINE = { stroke: 'rgba(255,255,255,0.12)' };

const allOn = (keys) => Object.fromEntries(keys.map((k) => [k, true]));

/* 파레토 x축 라벨 — 알람 이름(메시지)이 길어 겹치므로 기울여 그리고 일정 길이 넘으면 줄인다. 전체 이름·코드는 툴팁에 나온다 */
const PARETO_LABEL_MAX = 16;
const shortCode = (code) => {
  const s = String(code ?? '');
  return s.length > PARETO_LABEL_MAX ? `${s.slice(0, PARETO_LABEL_MAX - 1)}…` : s;
};
function ParetoTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{payload?.value}</title>
      <text x={0} y={0} dy={10} textAnchor="end" transform="rotate(-35)" fill="#C5D7E0" fontSize={11}>
        {shortCode(payload?.value)}
      </text>
    </g>
  );
}

function Check({ checked, onChange, label }) {
  return (
    <label className="dl-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="dl-check-box" aria-hidden="true"><span className="material-symbols-outlined">check</span></span>
      <span className="dl-check-text">{label}</span>
    </label>
  );
}

/* 열 선택 — 셀렉트처럼 생긴 버튼을 누르면 열 체크 목록이 열린다 */
function ColumnPicker({ label, columns, visible, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const count = columns.filter((c) => visible[c.key]).length;
  return (
    <div className="dl-colpicker" ref={ref}>
      <button type="button" className={`dl-colpicker-btn ${open ? 'open' : ''}`} onClick={() => setOpen((v) => !v)}>
        {label}{count < columns.length ? ` (${count}/${columns.length})` : ''}
      </button>
      {open && (
        <div className="dl-colpicker-pop">
          {columns.map((c) => <Check key={c.key} checked={!!visible[c.key]} label={c.label} onChange={() => onToggle(c.key)} />)}
        </div>
      )}
    </div>
  );
}

function DateSelect({ label, value, options, onChange, allLabel }) {
  return (
    <label className="dl-field dl-field-inline">
      <span className="dl-field-label">{label}</span>
      <select className="dl-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="all">{allLabel}</option>
        {options.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </label>
  );
}

function DataLog({ equipmentId, factories = [], machines = [], currentMachine = null, onSelectMachine, initialSubTab = 'shots' }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.dataLog.${key}`, params), [t]);
  const colLabel = useCallback((labelKey) => t(`components.monitoringWatch2.cols.${labelKey}`), [t]);

  const [subTab, setSubTab] = useState(SUB_TABS.includes(initialSubTab) ? initialSubTab : 'shots');
  const [shots, setShots] = useState(null);
  const [drive, setDrive] = useState(null);
  const [alarm, setAlarm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /* 샷로그 필터 */
  const [shotLimit, setShotLimit] = useState(100);
  const [shotSearch, setShotSearch] = useState('');
  const [shotCraft, setShotCraft] = useState('all');
  const [shotDate, setShotDate] = useState('all');
  const [shotRange, setShotRange] = useState('all');
  const [sampleInterval, setSampleInterval] = useState(1);
  const [onlyOut, setOnlyOut] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => allOn(SHOT_COLUMNS.map((c) => c.key)));
  const resetShotFilters = () => {
    setShotLimit(100); setShotSearch(''); setShotCraft('all'); setShotDate('all'); setShotRange('all');
    setSampleInterval(1); setOnlyOut(false); setVisibleCols(allOn(SHOT_COLUMNS.map((c) => c.key)));
  };
  /* 구동부 필터 */
  const [driveSearch, setDriveSearch] = useState('');
  const [driveDate, setDriveDate] = useState('all');
  const [driveCols, setDriveCols] = useState(() => allOn(DRIVE_COLUMNS));
  /* 알람 필터 */
  const [alarmSearch, setAlarmSearch] = useState('');
  const [alarmDate, setAlarmDate] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [alarmCols, setAlarmCols] = useState(() => allOn(ALARM_COLUMNS));

  const load = useCallback(async (silent) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      if (subTab === 'shots') setShots(await fetchMachineWatch2(equipmentId, shotLimit));
      else if (subTab === 'drive') setDrive(await fetchMachineDrive(equipmentId));
      else setAlarm(await fetchMachineAlarm(equipmentId));
    } catch (e) {
      console.error('DataLog load failed:', subTab, e);
      setError(tr('loadFailed'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [equipmentId, subTab, shotLimit, tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);

  /* ---------------- 공장 · 설비 (헤더 선택과 연동) ---------------- */
  const factoryKeyOf = (m) => (m?.factoryId ? `id:${m.factoryId}` : m?.factoryName ? `name:${m.factoryName}` : '');
  const currentFactoryKey = factoryKeyOf(currentMachine);
  const factoryOptions = useMemo(() => {
    const keys = new Map();
    machines.forEach((m) => {
      const key = factoryKeyOf(m);
      if (!key || keys.has(key)) return;
      const f = factories.find((x) => (m.factoryId ? x.id === m.factoryId : x.factoryName === m.factoryName));
      keys.set(key, f?.factoryName || m.factoryName || key);
    });
    return [...keys.entries()].map(([key, name]) => ({ key, name }));
  }, [machines, factories]);
  const machineOptions = useMemo(
    () => (currentFactoryKey ? machines.filter((m) => factoryKeyOf(m) === currentFactoryKey) : machines),
    [machines, currentFactoryKey]
  );
  const selectFactory = (key) => {
    const first = machines.find((m) => factoryKeyOf(m) === key);
    if (first && onSelectMachine) onSelectMachine(first);
  };
  const selectMachine = (id) => {
    const m = machines.find((x) => String(x.id) === String(id));
    if (m && onSelectMachine) onSelectMachine(m);
  };

  /* ---------------- 샷로그 파생 ---------------- */
  const shotList = useMemo(() => shots?.shots || [], [shots]);
  const craftOptions = useMemo(() => [...new Set(shotList.map((s) => s.craftId).filter(Boolean))], [shotList]);
  const shotDateOptions = useMemo(() => [...new Set(shotList.map((s) => dateOf(s.shotTime)).filter(Boolean))], [shotList]);
  const rangeOptions = useMemo(() => {
    const counts = shotList.map((s) => s.shotCount).filter(isNum).map(Number);
    if (counts.length === 0) return [];
    const lo = Math.floor((Math.min(...counts) - 1) / SHOT_RANGE_SIZE) * SHOT_RANGE_SIZE + 1;
    const hi = Math.max(...counts);
    const out = [];
    for (let s = lo; s <= hi; s += SHOT_RANGE_SIZE) out.push({ from: s, to: s + SHOT_RANGE_SIZE - 1 });
    return out.map((r) => ({ key: `${r.from}-${r.to}`, label: `${r.from}~${r.to}`, ...r }));
  }, [shotList]);
  const shotRows = useMemo(() => {
    /* 열별 평균·표준편차 (불러온 샷 전체 기준) → 이탈 판정 */
    const stats = {};
    SHOT_COLUMNS.forEach((c) => {
      const vals = shotList.map((s) => s?.[c.key]).filter(isNum).map(Number);
      if (vals.length >= 2) {
        const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
        const sd = Math.sqrt(vals.reduce((a, b) => a + (b - mean) * (b - mean), 0) / vals.length);
        stats[c.key] = { lo: mean - SIGMA_K * sd, hi: mean + SIGMA_K * sd };
      }
    });
    const range = rangeOptions.find((r) => r.key === shotRange) || null;
    const q = shotSearch.trim().toLowerCase();
    return shotList
      .filter((s) => shotCraft === 'all' || s.craftId === shotCraft)
      .filter((s) => shotDate === 'all' || dateOf(s.shotTime) === shotDate)
      .filter((s) => !range || (isNum(s.shotCount) && Number(s.shotCount) >= range.from && Number(s.shotCount) <= range.to))
      .filter((_, i) => i % sampleInterval === 0)
      /* 검색: 샷카운트·품번·표시 중인 열의 값 문자열에 포함되는지 */
      .filter((s) => !q || [s?.shotCount, s?.craftId, ...SHOT_COLUMNS.filter((c) => visibleCols[c.key]).map((c) => s?.[c.key])]
        .some((v) => v != null && String(v).toLowerCase().includes(q)))
      .map((s, i) => {
        const outCols = SHOT_COLUMNS.filter((c) => visibleCols[c.key] && stats[c.key] && isNum(s?.[c.key])
          && (Number(s[c.key]) > stats[c.key].hi || Number(s[c.key]) < stats[c.key].lo)).map((c) => c.key);
        return { ...s, no: i + 1, outCols };
      });
  }, [shotList, rangeOptions, shotRange, shotSearch, shotCraft, shotDate, sampleInterval, visibleCols]);
  const shotOutCount = shotRows.filter((r) => r.outCols.length > 0).length;
  const shotView = onlyOut ? shotRows.filter((r) => r.outCols.length > 0) : shotRows;
  const activeCols = SHOT_COLUMNS.filter((c) => visibleCols[c.key]);

  /* ---------------- 구동부 파생 ---------------- */
  const driveList = useMemo(() => drive?.driveHistory || [], [drive]);
  const driveDateOptions = useMemo(() => [...new Set(driveList.map((d) => dateOf(d.time)).filter(Boolean))], [driveList]);
  const driveRows = useMemo(() => {
    const q = driveSearch.trim().toLowerCase();
    return driveList.filter((d) => (driveDate === 'all' || dateOf(d.time) === driveDate)
      && (!q || `${d.driveType || ''} ${d.action || ''}`.toLowerCase().includes(q)));
  }, [driveList, driveSearch, driveDate]);
  const driveColumnDefs = DRIVE_COLUMNS.map((k) => ({ key: k, label: tr(k) }));

  /* ---------------- 알람 파생 ---------------- */
  const alarmList = useMemo(() => alarm?.alarmHistory || [], [alarm]);
  const alarmDateOptions = useMemo(() => [...new Set(alarmList.map((a) => dateOf(a.time)).filter(Boolean))], [alarmList]);
  const alarmRows = useMemo(() => {
    const q = alarmSearch.trim().toLowerCase();
    return alarmList.filter((a) => (severity === 'all' || a.severity === severity)
      && (alarmDate === 'all' || dateOf(a.time) === alarmDate)
      && (!q || `${a.code || ''} ${a.message || ''}`.toLowerCase().includes(q)));
  }, [alarmList, alarmSearch, alarmDate, severity]);
  const alarmColumnDefs = ALARM_COLUMNS.map((k) => ({ key: k, label: tr(k) }));
  /* 파레토: x축은 코드 대신 알람 이름 — 백엔드 name(전체 알람 기준 최신 메시지) 우선, 없으면 이력 50건에서 찾고, 그래도 없으면 코드.
   * 막대색은 코드별 심각도를 이력에서 찾아 불량(CRITICAL) 빨강, 그 외 노랑 */
  const paretoData = useMemo(() => {
    const criticalCodes = new Set(alarmList.filter((a) => a.severity === 'CRITICAL').map((a) => a.code));
    const nameByCode = {};
    alarmList.forEach((a) => { if (a.code && a.message && !nameByCode[a.code]) nameByCode[a.code] = a.message; });
    return (alarm?.paretoAnalysis?.data || []).map((d) => ({
      code: d.code,
      name: d.name || nameByCode[d.code] || d.code || '—',
      count: isNum(d.count) ? Number(d.count) : 0,
      cumulative: isNum(d.cumulative) ? Number(d.cumulative) : null,
      tone: criticalCodes.has(d.code) ? 'err' : 'wrn'
    }));
  }, [alarm, alarmList]);
  /* 타임라인 막대색: 그날 알람 이력의 최고 심각도 (이력이 없는 날은 회색) */
  const timelineData = useMemo(() => {
    const worstByDay = {};
    const rank = { CRITICAL: 2, WARNING: 1 };
    alarmList.forEach((a) => {
      const day = monthDayOf(a.time);
      if (!day) return;
      const r = rank[a.severity] || 0;
      if (r > (worstByDay[day] || 0)) worstByDay[day] = r;
    });
    return (alarm?.timeline?.data || []).map((d) => ({
      time: d.time,
      count: isNum(d.count) ? Number(d.count) : 0,
      tone: worstByDay[d.time] === 2 ? 'err' : worstByDay[d.time] === 1 ? 'wrn' : 'base'
    }));
  }, [alarm, alarmList]);

  const currentData = subTab === 'shots' ? shots : subTab === 'drive' ? drive : alarm;

  /* ---------------- 렌더 ---------------- */
  const renderSubTabs = () => (
    <div className="dl-subtabs">
      {SUB_TABS.map((key) => (
        <button key={key} type="button" className={`dl-subtab ${subTab === key ? 'active' : ''}`} onClick={() => setSubTab(key)}>
          {tr(`subTab.${key}`)}
        </button>
      ))}
    </div>
  );

  const renderShots = () => (
    <div className="dl-layout">
      <aside className="dl-panel dl-filter">
        <div className="dl-filter-head">
          <h3 className="dl-panel-title">{tr('filter')}</h3>
          <button type="button" className="dl-btn-primary dl-btn-sm" onClick={resetShotFilters}>
            <span className="material-symbols-outlined" aria-hidden="true">refresh</span>{tr('reset')}
          </button>
        </div>
        <label className="dl-search dl-search-block">
          <span className="material-symbols-outlined" aria-hidden="true">search</span>
          <input type="text" value={shotSearch} placeholder={tr('searchPlaceholder')} onChange={(e) => setShotSearch(e.target.value)} />
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('factory')}</span>
          <select className="dl-select" value={currentFactoryKey} onChange={(e) => selectFactory(e.target.value)} disabled={factoryOptions.length === 0}>
            {factoryOptions.length === 0 && <option value="">{tr('all')}</option>}
            {factoryOptions.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
          </select>
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('machine')}</span>
          <select className="dl-select" value={currentMachine?.id ?? equipmentId ?? ''} onChange={(e) => selectMachine(e.target.value)} disabled={machineOptions.length === 0}>
            {machineOptions.length === 0 && <option value="">{tr('all')}</option>}
            {machineOptions.map((m) => <option key={m.id} value={m.id}>{m.equipmentName || m.name || m.id}</option>)}
          </select>
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('product')}</span>
          <select className="dl-select" value={shotCraft} onChange={(e) => setShotCraft(e.target.value)}>
            <option value="all">{tr('all')}</option>
            {craftOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('period')}</span>
          <select className="dl-select" value={shotDate} onChange={(e) => setShotDate(e.target.value)}>
            <option value="all">{tr('all')}</option>
            {shotDateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('shotRange')}</span>
          <select className="dl-select" value={shotRange} onChange={(e) => setShotRange(e.target.value)}>
            <option value="all">{tr('all')}</option>
            {rangeOptions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('sampleInterval')}</span>
          <select className="dl-select" value={sampleInterval} onChange={(e) => setSampleInterval(Number(e.target.value))}>
            {SAMPLE_INTERVALS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="dl-field">
          <span className="dl-field-label">{tr('limit')}</span>
          <select className="dl-select" value={shotLimit} onChange={(e) => setShotLimit(Number(e.target.value))}>
            {SHOT_LIMITS.map((n) => <option key={n} value={n}>{tr('limitOption', { n })}</option>)}
          </select>
        </label>
        <div className="dl-divider" />
        <div className="dl-check-list">
          {SHOT_COLUMNS.map((c) => (
            <Check key={c.key} checked={!!visibleCols[c.key]} label={colLabel(c.labelKey)}
              onChange={() => setVisibleCols((prev) => ({ ...prev, [c.key]: !prev[c.key] }))} />
          ))}
        </div>
      </aside>

      <section className="dl-panel dl-content">
        <div className="dl-panel-head">
          <div className="dl-title-group">
            <h3 className="dl-panel-title">{tr('shotsTitle')}</h3>
            <span className="dl-subtitle">
              {tr('totalCount', { n: shotRows.length })} • {tr('outCount', { n: shotOutCount })}
              <em className="dl-tag">{tr('estimated')}</em>
            </span>
          </div>
          <Check checked={onlyOut} onChange={() => setOnlyOut((v) => !v)} label={tr('onlyOut')} />
        </div>
        {shotView.length === 0 ? (
          <div className="dl-message">{tr('noData')}</div>
        ) : (
          <div className="dl-scroll">
            <table className="dl-table">
              <thead>
                <tr>
                  <th>{tr('no')}</th>
                  <th>{colLabel('shotCount')}</th>
                  {activeCols.map((c) => <th key={c.key} title={c.unit}>{colLabel(c.labelKey)}</th>)}
                </tr>
              </thead>
              <tbody>
                {shotView.map((r) => (
                  <tr key={`${r.shotCount}-${r.no}`} title={`${fmtDateTime(r.shotTime)}${r.craftId ? ` · ${r.craftId}` : ''}`}>
                    <td>{r.no}</td>
                    <td>{intFmt(r.shotCount)}</td>
                    {activeCols.map((c) => (
                      <td key={c.key} className={r.outCols.includes(c.key) ? 'dl-out' : ''}>{num(r[c.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dl-note">{tr('outNote', { k: SIGMA_K })} · {tr('unitNote')}</div>
      </section>
    </div>
  );

  const renderDrive = () => (
    <section className="dl-panel">
      <div className="dl-panel-head">
        <div className="dl-title-group">
          <h3 className="dl-panel-title">{tr('driveTitle')}</h3>
          <span className="dl-subtitle">{tr('totalCount', { n: driveRows.length })}</span>
        </div>
      </div>
      <div className="dl-toolbar">
        <div className="dl-toolbar-left">
          <label className="dl-search">
            <span className="material-symbols-outlined" aria-hidden="true">search</span>
            <input type="text" value={driveSearch} placeholder={tr('searchPlaceholder')} onChange={(e) => setDriveSearch(e.target.value)} />
          </label>
          <DateSelect label={tr('period')} value={driveDate} options={driveDateOptions} onChange={setDriveDate} allLabel={tr('all')} />
          {/* 구동부 이력에는 심각도 정보가 없어 "전체"만 두고 비활성 */}
          <label className="dl-field dl-field-inline" title={tr('severityNotInDrive')}>
            <span className="dl-field-label">{tr('severity')}</span>
            <select className="dl-select" value="all" disabled onChange={() => {}}>
              <option value="all">{tr('all')}</option>
            </select>
          </label>
        </div>
        <div className="dl-toolbar-right">
          <ColumnPicker label={tr('columnsPick')} columns={driveColumnDefs} visible={driveCols}
            onToggle={(k) => setDriveCols((prev) => ({ ...prev, [k]: !prev[k] }))} />
          <button type="button" className="dl-btn-primary" disabled={driveRows.length === 0}
            onClick={() => downloadCsv(`drive-history-${equipmentId}.csv`,
              driveColumnDefs.filter((c) => driveCols[c.key]).map((c) => c.label),
              driveRows.map((d) => driveColumnDefs.filter((c) => driveCols[c.key]).map((c) => (
                c.key === 'time' ? fmtDateTime(d.time) : c.key === 'driveType' ? d.driveType : c.key === 'action' ? d.action : num(d[c.key], 1)
              ))))}>
            <span className="material-symbols-outlined" aria-hidden="true">download</span>CSV
          </button>
        </div>
      </div>
      {driveRows.length === 0 ? (
        <div className="dl-message">{tr('noData')}</div>
      ) : (
        <div className="dl-scroll">
          <table className="dl-table">
            <thead>
              <tr>
                {driveCols.time && <th>{tr('time')}</th>}
                {driveCols.driveType && <th>{tr('driveType')}</th>}
                {driveCols.action && <th>{tr('action')}</th>}
                {driveCols.pressure && <th>{tr('pressure')} (MPa)</th>}
                {driveCols.speed && <th>{tr('speed')} (mm/s)</th>}
                {driveCols.position && <th>{tr('position')} (mm)</th>}
              </tr>
            </thead>
            <tbody>
              {driveRows.map((d, i) => (
                <tr key={`${d.time}-${i}`}>
                  {driveCols.time && <td>{fmtTime(d.time)}</td>}
                  {driveCols.driveType && <td>{d.driveType || '—'}</td>}
                  {driveCols.action && <td>{d.action || '—'}</td>}
                  {driveCols.pressure && <td>{num(d.pressure, 1)}</td>}
                  {driveCols.speed && <td>{num(d.speed, 1)}</td>}
                  {driveCols.position && <td>{num(d.position, 1)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  const renderAlarms = () => {
    const summary = alarm?.summary || {};
    const statusCell = (a) => (
      <>
        {a.status === 'resolved' && <span className="dl-badge dl-badge-status">{tr('statusResolved')}</span>}
        {a.status === 'active' && <span className="dl-badge dl-badge-critical">{tr('statusActive')}</span>}
        {a.status !== 'resolved' && a.status !== 'active' && (a.status || '—')}
      </>
    );
    const statusText = (a) => (a.status === 'resolved' ? tr('statusResolved') : a.status === 'active' ? tr('statusActive') : (a.status || ''));
    return (
      <section className="dl-panel">
        <div className="dl-panel-head">
          <div className="dl-title-group">
            <h3 className="dl-panel-title">{tr('alarmsTitle')}</h3>
            <span className="dl-subtitle">{tr('totalCount', { n: alarmList.length })}</span>
          </div>
          <div className="dl-badge-group">
            <span className="dl-badge dl-badge-critical">{tr('critical')} {intFmt(summary.critical)}</span>
            <span className="dl-badge dl-badge-warning">{tr('warning')} {intFmt(summary.warning)}</span>
            <span className="dl-badge dl-badge-info">{tr('info')} {intFmt(summary.info)}</span>
          </div>
        </div>
        <div className="dl-toolbar">
          <div className="dl-toolbar-left">
            <label className="dl-search">
              <span className="material-symbols-outlined" aria-hidden="true">search</span>
              <input type="text" value={alarmSearch} placeholder={tr('searchPlaceholder')} onChange={(e) => setAlarmSearch(e.target.value)} />
            </label>
            <DateSelect label={tr('period')} value={alarmDate} options={alarmDateOptions} onChange={setAlarmDate} allLabel={tr('all')} />
            <label className="dl-field dl-field-inline">
              <span className="dl-field-label">{tr('severity')}</span>
              <select className="dl-select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="all">{tr('all')}</option>
                {SEVERITIES.map((s) => <option key={s} value={s}>{tr(SEVERITY_TONE[s])}</option>)}
              </select>
            </label>
          </div>
          <div className="dl-toolbar-right">
            <ColumnPicker label={tr('columnsPick')} columns={alarmColumnDefs} visible={alarmCols}
              onToggle={(k) => setAlarmCols((prev) => ({ ...prev, [k]: !prev[k] }))} />
            <button type="button" className="dl-btn-primary" disabled={alarmRows.length === 0}
              onClick={() => downloadCsv(`alarm-history-${equipmentId}.csv`,
                alarmColumnDefs.filter((c) => alarmCols[c.key]).map((c) => c.label),
                alarmRows.map((a) => alarmColumnDefs.filter((c) => alarmCols[c.key]).map((c) => (
                  c.key === 'occurredAt' ? fmtDateTime(a.time) : c.key === 'severity' ? tr(SEVERITY_TONE[a.severity] || 'info')
                    : c.key === 'status' ? statusText(a) : a[c.key]
                ))))}>
              <span className="material-symbols-outlined" aria-hidden="true">download</span>CSV
            </button>
          </div>
        </div>
        {alarmRows.length === 0 ? (
          <div className="dl-message">{tr('noData')}</div>
        ) : (
          <div className="dl-scroll">
            <table className="dl-table dl-table-left">
              <thead>
                <tr>
                  {alarmCols.occurredAt && <th>{tr('occurredAt')}</th>}
                  {alarmCols.severity && <th>{tr('severity')}</th>}
                  {alarmCols.code && <th className="dl-th-left">{tr('code')}</th>}
                  {alarmCols.message && <th className="dl-th-wide">{tr('message')}</th>}
                  {alarmCols.settingValue && <th>{tr('settingValue')}</th>}
                  {alarmCols.actualValue && <th>{tr('actualValue')}</th>}
                  {alarmCols.status && <th>{tr('status')}</th>}
                </tr>
              </thead>
              <tbody>
                {alarmRows.map((a, i) => (
                  <tr key={`${a.time}-${a.code}-${i}`}>
                    {alarmCols.occurredAt && <td>{fmtDateTime(a.time)}</td>}
                    {alarmCols.severity && <td><span className={`dl-badge dl-badge-${SEVERITY_TONE[a.severity] || 'info'}`}>{tr(SEVERITY_TONE[a.severity] || 'info')}</span></td>}
                    {alarmCols.code && <td className="dl-code dl-td-left">{a.code || '—'}</td>}
                    {alarmCols.message && <td className="dl-td-wide">{a.message || '—'}</td>}
                    {alarmCols.settingValue && <td>{a.settingValue ?? '—'}</td>}
                    {alarmCols.actualValue && <td>{a.actualValue ?? '—'}</td>}
                    {alarmCols.status && <td>{statusCell(a)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  const renderAnalysis = () => (
    <div className="dl-grid-2">
      <section className="dl-panel dl-panel-tall">
        <div className="dl-panel-head">
          <h3 className="dl-panel-title">{tr('paretoTitle')}</h3>
          <span className="dl-legend">
            <span className="dl-legend-item"><i style={{ background: COLORS.err }} />{tr('critical')}</span>
            <span className="dl-legend-item"><i style={{ background: COLORS.wrn }} />{tr('errorCode')}</span>
            <span className="dl-note">{tr('paretoNote')}</span>
          </span>
        </div>
        {paretoData.length === 0 ? <div className="dl-message">{tr('noData')}</div> : (
          <div className="dl-chart dl-chart-tall">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={paretoData} margin={{ top: 24, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={<ParetoTick />} axisLine={AXIS_LINE} tickLine={false} interval={0} height={96} />
                <YAxis yAxisId="l" tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
                <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} unit="%" />
                <Tooltip {...TOOLTIP_STYLE} labelFormatter={(label, payload) => {
                  const code = payload?.[0]?.payload?.code;
                  return code && code !== label ? `${label} (${code})` : label;
                }} />
                <ReferenceLine yAxisId="r" y={80} stroke="#98A8BF" strokeDasharray="4 3" label={{ value: tr('baseline80'), position: 'insideTopRight', fill: '#C5D7E0', fontSize: 11 }} />
                <Bar yAxisId="l" dataKey="count" name={tr('count')} radius={[4, 4, 0, 0]} maxBarSize={68} isAnimationActive={false}>
                  {paretoData.map((d, i) => <Cell key={`${d.code}-${i}`} fill={COLORS[d.tone]} />)}
                  <LabelList dataKey="count" position="top" fill="#FFFFFF" fontSize={12} />
                </Bar>
                <Line yAxisId="r" type="monotone" dataKey="cumulative" name={tr('cumulative')} stroke={COLORS.line} strokeWidth={1.5} dot={{ r: 3 }} isAnimationActive={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
      <section className="dl-panel dl-panel-tall">
        <div className="dl-panel-head">
          <h3 className="dl-panel-title">{tr('timelineTitle')}</h3>
          <span className="dl-note">{tr('timelineNote')}</span>
        </div>
        {timelineData.length === 0 ? <div className="dl-message">{tr('noData')}</div> : (
          <div className="dl-chart dl-chart-tall">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timelineData} margin={{ top: 24, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="time" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} minTickGap={16} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
                <Tooltip {...TOOLTIP_STYLE} />
                <Bar dataKey="count" name={tr('count')} radius={[4, 4, 0, 0]} maxBarSize={20} isAnimationActive={false}>
                  {timelineData.map((d) => <Cell key={d.time} fill={COLORS[d.tone]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
    </div>
  );

  return (
    <div className="dl-root">
      {renderSubTabs()}
      {isLoading && !currentData && <div className="dl-message">{tr('loading')}</div>}
      {error && <div className="dl-message dl-message-error">{error}</div>}
      {(!isLoading || currentData) && (
        <>
          {subTab === 'shots' && renderShots()}
          {subTab === 'drive' && renderDrive()}
          {subTab === 'alarms' && renderAlarms()}
          {subTab === 'analysis' && renderAnalysis()}
        </>
      )}
    </div>
  );
}

export default DataLog;
